import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, Modal, Badge } from "@/components/Kit";
import { IndianRupee, Plus, Trash2, CheckCircle2, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAcademicYear } from "@/lib/academicYear";
import { fmtDate } from "@/lib/date";
import OnlineOrders from "./OnlineOrders";
import ReceiptTemplate from "@/components/print/ReceiptTemplate";
import { useTemplates, Scaled, PrintButton } from "@/components/print/PrintKit";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");

const FEE_HEADS = ["Admission Fee", "Tuition Fee", "Monthly Fee", "Transport Fee", "Lab Fee", "Library Fee", "Sports Fee", "Computer Fee"];

export default function Fees() {
  const qc = useQueryClient();
  const { school } = useAuth();
  const { current: ay } = useAcademicYear();
  const [open, setOpen] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [mode, setMode] = useState("cash");
  const [heads, setHeads] = useState([{ name: "Tuition Fee", amount: "" }]);
  const [receipt, setReceipt] = useState(null);
  const [remarks, setRemarks] = useState("");
  const { data: tpl } = useTemplates();
  const copies = Number(tpl?.receipt?.copies) || 2;
  const copyLabels = tpl?.receipt?.copy_labels || ["SCHOOL COPY", "PARENT COPY"];

  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/school/students")).data });
  const { data: receipts = [] } = useQuery({ queryKey: ["receipts"], queryFn: async () => (await api.get("/school/fees/receipts")).data });

  const pay = useMutation({
    mutationFn: async () => {
      const clean = heads.filter((h) => h.name && Number(h.amount) > 0).map((h) => ({ name: h.name, amount: Number(h.amount), period: h.period || "" }));
      if (!studentId || !clean.length) throw { response: { data: { detail: "Select student and add at least one fee head" } } };
      return (await api.post("/school/fees/pay", { student_id: studentId, heads: clean, mode, remarks, academic_year: ay || tpl?.receipt?.academic_year || "" })).data;
    },
    onSuccess: (r) => {
      toast.success(`Receipt ${r.receipt_no} generated · WhatsApp sent to parent`);
      setReceipt(r);
      setOpen(false);
      setHeads([{ name: "Tuition Fee", amount: "" }]);
      setStudentId("");
      setRemarks("");
      qc.invalidateQueries({ queryKey: ["receipts"] });
      qc.invalidateQueries({ queryKey: ["students"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const total = heads.reduce((a, h) => a + (Number(h.amount) || 0), 0);
  const rcpt = receipt && { ...(students.find((s) => s.id === receipt.student_id) || {}), ...receipt };
  const selected = students.find((s) => s.id === studentId);
  const balance = selected ? Math.max(0, (selected.total_fee || 0) - (selected.paid_fee || 0)) : 0;

  return (
    <div data-testid="fees-page">
      <PageHeader
        title="Fee Management"
        subtitle="Collect fees, auto-generate receipts & notify parents"
        action={<button className="ep-btn" onClick={() => setOpen(true)} data-testid="collect-fee-btn"><Plus size={16} /> Collect Fee</button>}
      />

      <div className="ep-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Receipt No</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Class</th>
                <th className="px-4 py-3">Mode</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`receipt-row-${r.receipt_no}`}>
                  <td className="px-4 py-3 font-mono text-xs text-indigo-300">{r.receipt_no}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-white">{r.student_name}</td>
                  <td className="px-4 py-3 text-slate-400">{r.class_name}-{r.section}</td>
                  <td className="px-4 py-3"><Badge color="#06B6D4">{r.mode}</Badge></td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-400">{inr(r.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setReceipt(r)} className="rounded-lg bg-indigo-500/15 p-2 text-indigo-300 hover:bg-indigo-500/25" data-testid={`view-receipt-${r.receipt_no}`}><Printer size={15} /></button>
                  </td>
                </tr>
              ))}
              {!receipts.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No fee receipts yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <OnlineOrders />

      {/* Collect modal */}
      <Modal open={open} onClose={() => setOpen(false)} title="Collect Fee" testid="collect-fee-modal">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-slate-400">Student</span>
          <select className="ep-input" value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="fee-student-select">
            <option value="">Select student…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.class_name}-{s.section})</option>)}
          </select>
        </label>

        {selected && (
          <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl border border-slate-800 bg-slate-900/40 p-3 text-center" data-testid="fee-student-balance">
            <div><p className="text-[11px] text-slate-500">Total fee</p><p className="text-sm font-semibold text-white">{inr(selected.total_fee)}</p></div>
            <div><p className="text-[11px] text-slate-500">Paid</p><p className="text-sm font-semibold text-emerald-400">{inr(selected.paid_fee)}</p></div>
            <div><p className="text-[11px] text-slate-500">Balance</p><p className="text-sm font-semibold text-rose-400">{inr(balance)}</p></div>
          </div>
        )}

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-400">Fee Heads</span>
            {selected && balance > 0 && (
              <button
                type="button"
                onClick={() => setHeads([{ name: "Tuition Fee", amount: String(balance), period: "" }])}
                className="text-[11px] font-medium text-indigo-300 hover:text-indigo-200"
                data-testid="fee-fill-balance"
              >
                Fill balance ({inr(balance)})
              </button>
            )}
          </div>
          {heads.map((h, i) => (
            <div key={i} className="flex gap-2" data-testid={`fee-head-${i}`}>
              <select className="ep-input min-w-0 flex-1" value={h.name} onChange={(e) => setHeads(heads.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}>
                {FEE_HEADS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <input className="ep-input w-20 shrink-0" placeholder="Period" value={h.period || ""} onChange={(e) => setHeads(heads.map((x, j) => j === i ? { ...x, period: e.target.value } : x))} data-testid={`fee-period-${i}`} />
              <input type="number" className="ep-input w-24 shrink-0" placeholder="₹" value={h.amount} onChange={(e) => setHeads(heads.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} data-testid={`fee-amount-${i}`} />
              <button className="ep-btn-ghost ep-btn shrink-0 px-3" onClick={() => setHeads(heads.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
            </div>
          ))}
          <button className="ep-btn-ghost ep-btn text-xs" onClick={() => setHeads([...heads, { name: "Admission Fee", amount: "" }])} data-testid="add-fee-head"><Plus size={14} /> Add head</button>
        </div>

        <label className="mt-4 block">
          <span className="mb-1.5 block text-xs font-medium text-slate-400">Payment Mode</span>
          <select className="ep-input" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="fee-mode-select">
            <option value="cash">Cash</option>
            <option value="phonepe">PhonePe</option>
            <option value="upi">UPI</option>
            <option value="online">Online</option>
          </select>
        </label>

        <label className="mt-3 block"><span className="mb-1.5 block text-xs font-medium text-slate-400">Remarks (printed on receipt)</span><input className="ep-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="fee-remarks" /></label>
        <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-800 px-4 py-3">
          <span className="text-sm text-slate-400">Total</span>
          <span className="text-xl font-bold font-head text-white">{inr(total)}</span>
        </div>

        <button className="ep-btn mt-4 w-full" disabled={pay.isPending} onClick={() => pay.mutate()} data-testid="submit-fee-btn">
          {pay.isPending ? "Processing…" : <><CheckCircle2 size={16} /> Receive Payment</>}
        </button>
        {mode === "phonepe" && <p className="mt-2 text-center text-xs text-amber-400">PhonePe gateway is MOCKED — share merchant credentials to go live.</p>}
      </Modal>

      {/* Receipt view */}
      <Modal open={!!receipt} onClose={() => setReceipt(null)} title={`Fee Receipt · ${receipt?.receipt_no || ""}`} testid="receipt-modal" wide>
        {receipt && (
          <>
            <div className="flex justify-center overflow-hidden rounded-xl bg-slate-200 p-3">
              <Scaled scale={0.95} width="8.27in" height="5.69in"><ReceiptTemplate receipt={rcpt} school={school} tpl={tpl} copyLabel={copyLabels[0]} /></Scaled>
            </div>
            <p className="mt-2 text-center text-[11px] text-slate-500">Prints {copies} cop{copies === 1 ? "y" : "ies"} · 8.27in × 5.69in · black & white</p>
            <PrintButton title={`Receipt ${receipt.receipt_no}`} size="8.27in 5.69in" grayscale label="Print Receipt" testid="print-receipt-btn">
              {Array.from({ length: copies }).map((_, i) => <ReceiptTemplate key={i} receipt={rcpt} school={school} tpl={tpl} copyLabel={copyLabels[i] || ""} />)}
            </PrintButton>
          </>
        )}
      </Modal>
    </div>
  );
}
